import { Component, EventEmitter, Injector, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, Card, CardPriorityEnum, CardTemperatureEnum, PRIORITY_LABEL, Product, Stage, TEMPERATURE_LABEL, URGENCY_CONTEXT } from 'lib-trend-core';
import { TransferCardComponent } from '../transfer-card/transfer-card.component';

@Component({
    selector: 'app-card-item',
    templateUrl: './card-item.component.html',
    styleUrl: './card-item.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CardItemComponent extends AbstractComponent {

  @Input() card!: Card;
  @Input() idStage!: string;
  @Input() cardIndex!: number
  @Input() stage!: Stage;

  @Output() cardClick = new EventEmitter<Card>();
  @Output() deleteCard = new EventEmitter<Card>();
  @Output() openCardModal = new EventEmitter<Stage | null>();
  @Output() editCard = new EventEmitter<Card>();
  /** Forwarded from the radar block (RF-12, RF-36). */
  @Output() scheduleNextAction = new EventEmitter<Card>();
  @Output() openScoreExplanation = new EventEmitter<Card>();
  /** E4 RF-17: re-emitted from the radar's next-action line to the column. */
  @Output() concludeNextAction = new EventEmitter<Card>();

  readonly CardTemperatureEnum = CardTemperatureEnum;

  /**
   * Lateral urgency stripe — the daily radar read down the edge of the column
   * (RF-13, RF-14).
   *
   * The badge inside the radar block carries the same state with an icon and a
   * label; this stripe is what makes a column of 30 deals scannable without
   * reading any of them. Colour is never the only carrier — the badge below it
   * always spells the state out (RNF-08).
   *
   * Empty for a closed deal and for a company without the CRM module: a won or
   * lost deal has no pending commitment, so an urgency edge would be noise.
   *
   * Returns a component class rather than Bootstrap utilities on purpose:
   * `border-{color}` and `border-3` both apply to all four sides, so the utility
   * pair would repaint the whole card instead of its edge. This is the
   * last-resort case the UI rules allow — the SCSS uses `var(--ct-*)` tokens, no
   * literal colour.
   */
  get urgencyEdgeClass(): string {
    if (!this.getModuleCRM()) return '';
    if (this.card?.won !== null && this.card?.won !== undefined) return '';

    const context = URGENCY_CONTEXT[this.card?.urgency];
    return context ? `card-urgency-edge card-urgency-edge--${context}` : '';
  }

  /**
   * The blacklist badge of a CLOSED deal (SPEC-2026-10 RF-21, ADR-002): informative, the deal
   * keeps counting and keeps its column. An open deal of a blacklisted contact is withdrawn from
   * the board by the API and never reaches this card.
   */
  get isBlacklistedClosed(): boolean {
    return this.card?.leadFlags?.blacklisted === true && (this.card?.won === true || this.card?.won === false);
  }

  constructor(
    injector: Injector,
    public dialog: MatDialog,
  ) {
    super(injector);
  }

  onCardClick(event: MouseEvent, card: Card): void {
    event.stopPropagation();
    this.cardClick.emit(card);
  }

  onDeleteCard(card: Card): void {
    this.deleteCard.emit(card);
  }

  onOpenCardModal(card: Card): void {
    this.openCardModal.emit(null);
  }

  onEditCard(card: Card): void {
    this.editCard.emit(card);
  }

  translateEnums(value: string): string {
    return PRIORITY_LABEL[value as CardPriorityEnum] ?? 'kanban.cardItem.priority';
  }

  /**
   * Temperature is DERIVED from the lead's qualification and health (E2 RF-22, RF-23):
   * the card shows it and explains it, and no longer offers to change it.
   */
  temperatureTooltip(): string {
    const temperature = this.card?.temperature as CardTemperatureEnum | undefined;
    return temperature && TEMPERATURE_LABEL[temperature]
      ? TEMPERATURE_LABEL[temperature]
      : 'kanban.cardItem.temperatureUnavailable';
  }

  getProductsNames(products: Array<Product>): string {
    return products?.filter(p => p?.name).map(p => p.name).join(', ') || '';
  }

  openTransferCardModal(event: MouseEvent, card: Card, action: 'move' | 'clone') {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(TransferCardComponent, {
      data: { idStage: this.idStage, card, action },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }
}
